import React from "react";
import { styled, Text, css } from "@sourceful/shared-components";
import { SanityProductFeature, SanityProductFeatureSection } from "@sourceful/shared-types";
import { getSanityImgUrl } from "@lib/sanity";
import toRem from "@styles/postprocess/conversion/to-rem";
import LearnMoreModal from "@components/LearnMoreModal";

const sectionFeatureListCss = css({
  padding: "0",
});

const ListItem = styled("li", {
  listStyleType: "none",
  mbe: "$block_m1",
  display: "flex",
  flexDirection: "row",

  "&:last-child": {
    mbe: "0",
  },
  "@md": {
    mbe: "$block_m3",
  },
});

const FeatureDescription = styled("div", {
  color: "#747477",
  fontSize: "14px",
  lineHeight: "160%",
});

const ResponsiveImageWrapper = styled("div", {
  marginTop: "10px",
  marginRight: "15px",
});

const ImageWrapper = styled("div", {
  borderRadius: toRem(8),
  height: toRem(40),
  width: toRem(40),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledImage = styled("img", {
  borderRadius: toRem(8),
});

const HeaderBadgeWrapper = styled("div", {
  display: "inline-block",
});

const BadgeWrapper = styled("div", {
  marginLeft: "10px",
  display: "inline-block",
  verticalAlign: "top",
});

const FeatureBadge = styled("div", {
  textTransform: "uppercase",
  padding: "5px 8px 5px 8px",
  borderRadius: "4px",
  fontSize: "10px",
  variants: {
    appearance: {
      fixed: {
        background: "$primary_80",
        color: "#FFFFFF",
      },
      option: {
        background: "#FFFFFF",
        border: "1px solid #B9B9BB",
        color: "#B9B9BB",
      },
    },
  },
});

interface ProductFeaturesListProps {
  features: SanityProductFeature[];
  backgroundTheme: string;
  iconTheme: string;
  colorOverride?: string;
}

const ProductFeaturesList = ({
  features,
  backgroundTheme,
  iconTheme,
  colorOverride,
}: ProductFeaturesListProps) => {
  const borderColor = colorOverride || "$palette3_colour1_tint60";

  return (
    <ol className={sectionFeatureListCss()}>
      {features.map(listItem => {
        const getIconColours = (
          theme: SanityProductFeatureSection["iconTheme"] | string
        ): { color: string; backgroundColor: string } => {
          switch (theme) {
            case "green":
              return { color: "#195839", backgroundColor: "#D0F1E1" };
            case "blue":
              return { color: "#17575E", backgroundColor: "#E3F6F8" };
            case "pink":
              return {
                color: "#CA466E",
                backgroundColor: "#FFE6EC",
              };
            case "purple":
              return { color: "#9730E8", backgroundColor: "#F4E8FD" };
            default:
              return {
                color: colorOverride ? colorOverride : "$palette2_colour2_shade20",
                backgroundColor: "$palette2_colour2_tint70",
              };
          }
        };
        const iconColours = getIconColours(iconTheme);
        const getAbbreviationFromTitle = (title: string) =>
          title
            .split(" ")
            .map((word, index) => (index === 0 ? word[0] : word[0].toLowerCase()))
            .slice(0, 2)
            .join("");
        return (
          <ListItem key={listItem.id}>
            <ResponsiveImageWrapper>
              <ImageWrapper
                css={{
                  ...iconColours,
                  backgroundColor:
                    backgroundTheme === "grey" ? "white" : iconColours.backgroundColor,
                  border: backgroundTheme === "grey" ? `1px solid ${borderColor}` : "",
                }}
              >
                {listItem.image ? (
                  <StyledImage
                    src={getSanityImgUrl(listItem.image?.asset._ref || "", base =>
                      base.width(40).height(40)
                    )}
                    height={40}
                    width={40}
                    loading="lazy"
                  />
                ) : (
                  listItem.imageFallbackText || getAbbreviationFromTitle(listItem.title)
                )}
              </ImageWrapper>
            </ResponsiveImageWrapper>
            <div>
              <HeaderBadgeWrapper>
                <Text level={2} css={{ fontWeight: 500 }}>
                  {listItem.title}
                </Text>
                {listItem.badge && (
                  <BadgeWrapper>
                    <FeatureBadge appearance={listItem.badge}>{listItem.badge}</FeatureBadge>
                  </BadgeWrapper>
                )}
              </HeaderBadgeWrapper>
              <FeatureDescription>{listItem.description}</FeatureDescription>

              {listItem.learnMoreModal && listItem.learnMoreModal.modal && (
                <LearnMoreModal learnMoreModal={listItem.learnMoreModal} />
              )}
            </div>
          </ListItem>
        );
      })}
    </ol>
  );
};

export default ProductFeaturesList;
